import {useCallback, useState} from 'react';
import {useCreateFollowUpDraftMutation, type FollowUpChannel} from '../../../../store/features/calls/callsService';
import {errorStatus} from '../insightsErrors';
import {logFollowUpDrafted} from '../insightsLog';

export interface FollowUpText {
    subject: string;
    body: string;
}

export interface FollowUpDraftState extends FollowUpText {
    channel: FollowUpChannel;
    generated: FollowUpText;
}

export interface FollowUpError {
    channel: FollowUpChannel;
    cause: unknown;
}

export interface FollowUpDrafts {
    draft: FollowUpDraftState | null;
    pending: FollowUpChannel | null;
    error: FollowUpError | null;
    edited: boolean;
    generate: (channel: FollowUpChannel) => Promise<void>;
    update: (patch: Partial<FollowUpText>) => void;
}

export const isEdited = (draft: FollowUpDraftState | null): boolean =>
    Boolean(draft && (draft.subject !== draft.generated.subject || draft.body !== draft.generated.body));

export const useFollowUpDrafts = (callId: number): FollowUpDrafts => {
    const [createDraft] = useCreateFollowUpDraftMutation();
    const [draft, setDraft] = useState<FollowUpDraftState | null>(null);
    const [pending, setPending] = useState<FollowUpChannel | null>(null);
    const [error, setError] = useState<FollowUpError | null>(null);

    const generate = useCallback(async (channel: FollowUpChannel) => {
        setPending(channel);
        setError(null);
        try {
            const result = await createDraft({callId, channel}).unwrap();
            const generated = {subject: result.subject ?? '', body: result.body ?? ''};
            setDraft({channel: result.channel ?? channel, ...generated, generated});
            logFollowUpDrafted({callId, channel, status: 201});
        } catch (cause) {
            setError({channel, cause});
            logFollowUpDrafted({callId, channel, status: errorStatus(cause) ?? 'unknown'});
        } finally {
            setPending(null);
        }
    }, [callId, createDraft]);

    const update = useCallback((patch: Partial<FollowUpText>) => {
        setDraft((current) => (current ? {...current, ...patch} : current));
    }, []);

    return {draft, pending, error, edited: isEdited(draft), generate, update};
};
